import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Radio,
  Typography,
} from "@mui/material";
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import DnsOutlinedIcon from "@mui/icons-material/DnsOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import PlaylistAddOutlinedIcon from "@mui/icons-material/PlaylistAddOutlined";
import AddUrlForm from "./AddUrlForm";
import AddBulkUrlForm from "./AddBulkUrlForm";

/* ---------------------------------------------------------
   Design tokens — kept in sync with Dashboard.jsx /
   SitemapGroups.jsx.
--------------------------------------------------------- */
const COLOR = {
  bg: "#F5F6F4",
  surface: "#FFFFFF",
  ink: "#161A1D",
  inkMuted: "#6B7280",
  inkFaint: "#9AA1A9",
  line: "#E4E7E7",
  accent: "#0B7285",
  accentSoft: "#E3F1F1",
};
const FONT_DISPLAY = "'Space Grotesk', sans-serif";

const SOURCE_OPTIONS = [
  {
    value: "wordpress",
    label: "WordPress",
    description: "Pages, posts, products, categories, and tags.",
    icon: LanguageOutlinedIcon,
  },
  {
    value: "node",
    label: "Node.js / Custom",
    description: "URLs you manage manually.",
    icon: DnsOutlinedIcon,
  },
];

const MODE_OPTIONS = [
  { value: "single", label: "Single URL", icon: LinkOutlinedIcon },
  { value: "bulk", label: "Bulk URLs", icon: PlaylistAddOutlinedIcon },
];

function ModeToggle({ mode, onChange }) {
  return (
    <Box
      role="radiogroup"
      aria-label="Add mode"
      display="inline-flex"
      sx={{ border: `1px solid ${COLOR.line}`, borderRadius: "8px", p: "3px", mb: 2 }}
    >
      {MODE_OPTIONS.map((option) => {
        const Icon = option.icon;
        const selected = mode === option.value;
        return (
          <Box
            key={option.value}
            role="radio"
            aria-checked={selected}
            tabIndex={0}
            onClick={() => onChange(option.value)}
            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onChange(option.value)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              px: "12px",
              py: "6px",
              borderRadius: "6px",
              cursor: "pointer",
              bgcolor: selected ? COLOR.accentSoft : "transparent",
              color: selected ? COLOR.accent : COLOR.inkMuted,
              fontSize: "13px",
              fontWeight: 600,
              userSelect: "none",
            }}
          >
            <Icon sx={{ fontSize: 16 }} />
            {option.label}
          </Box>
        );
      })}
    </Box>
  );
}

function SourceCard({ option, selected, onSelect }) {
  const Icon = option.icon;
  return (
    <Box
      role="radio"
      aria-checked={selected}
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onSelect()}
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: "12px",
        p: "14px 16px",
        borderRadius: "10px",
        border: `1.5px solid ${selected ? COLOR.accent : COLOR.line}`,
        bgcolor: selected ? COLOR.accentSoft : COLOR.surface,
        cursor: "pointer",
        transition: "border-color 0.15s ease, background-color 0.15s ease",
        "&:hover": { borderColor: COLOR.accent },
      }}
    >
      <Icon sx={{ fontSize: 22, color: COLOR.accent, mt: "2px" }} />
      <Box flex={1}>
        <Typography sx={{ fontWeight: 600, fontSize: "14.5px", color: COLOR.ink }}>
          {option.label}
        </Typography>
        <Typography sx={{ fontSize: "13px", color: COLOR.inkMuted, mt: "2px" }}>
          {option.description}
        </Typography>
      </Box>
      <Radio
        checked={selected}
        tabIndex={-1}
        sx={{
          p: 0,
          "&.Mui-checked": { color: COLOR.accent },
        }}
      />
    </Box>
  );
}

export default function AddUrlDialog({ open, onClose, onSuccess, editingUrl }) {
  const isEditing = Boolean(editingUrl);
  const [step, setStep] = useState(isEditing ? 2 : 1);
  const [source, setSource] = useState(editingUrl?.source ?? "");
  const [mode, setMode] = useState("single");

  // Re-sync whenever the dialog reopens, since editingUrl can
  // change between an "Add" click and an "Edit" click.
  useEffect(() => {
    if (open) {
      setStep(isEditing ? 2 : 1);
      setSource(editingUrl?.source ?? "");
      setMode("single");
    }
  }, [open, isEditing, editingUrl]);

  const handleClose = () => {
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: "14px" } }}
    >
      {step === 1 && (
        <>
          <DialogTitle sx={{ fontFamily: FONT_DISPLAY, fontWeight: 600, color: COLOR.ink }}>
            Add sitemap URL
          </DialogTitle>

          <DialogContent>
            <ModeToggle mode={mode} onChange={setMode} />

            <Typography sx={{ fontSize: "13.5px", color: COLOR.inkMuted, mb: 2 }}>
              Select where this URL is managed.
            </Typography>

            <Box role="radiogroup" display="flex" flexDirection="column" gap="10px">
              {SOURCE_OPTIONS.map((option) => (
                <SourceCard
                  key={option.value}
                  option={option}
                  selected={source === option.value}
                  onSelect={() => setSource(option.value)}
                />
              ))}
            </Box>
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            <Button onClick={handleClose} sx={{ textTransform: "none", color: COLOR.inkMuted }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              disableElevation
              disabled={!source}
              onClick={() => setStep(2)}
              sx={{
                bgcolor: COLOR.accent,
                textTransform: "none",
                fontWeight: 600,
                "&:hover": { bgcolor: "#095e6b" },
              }}
            >
              Continue
            </Button>
          </DialogActions>
        </>
      )}

      {step === 2 && mode === "bulk" && !isEditing && (
        <AddBulkUrlForm
          source={source}
          onBack={() => setStep(1)}
          onClose={handleClose}
          onSuccess={onSuccess}
        />
      )}

      {step === 2 && (mode === "single" || isEditing) && (
        <AddUrlForm
          source={source}
          editingUrl={editingUrl}
          onBack={() => setStep(1)}
          onClose={handleClose}
          onSuccess={onSuccess}
        />
      )}
    </Dialog>
  );
}